import { ReactElement, useMemo } from 'react';
import { Chip, Skeleton, Typography } from '@mui/material';

import { useGetMainTokenBalanceQuery } from 'modules/api/actions/getMainTokenBalance';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { BaseStat } from 'modules/common/components/BaseStat';
import { BorderedPaper } from 'modules/common/components/BorderedPaper';
import {
  BUY_MAIN_MORE_LINK,
  DECIMAL_PLACES_SHORT,
  DEFAULT_DECIMAL_PLACES,
  HUNDRED,
  ZERO,
} from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { MainTokenIcon } from 'modules/common/icons';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetLiquidStakingAPY } from 'modules/liquidStaking/hooks/useGetLiquidStakingAPY';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';

import buyMoreImage from '../../assets/buy-more.svg';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function Stats(): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);

  const { isConnected, connectionKey, cacheTime } = useConnection();
  const {
    data: mainTokenBalance = ZERO,
    isLoading: isMainTokenBalanceLoading,
  } = useGetMainTokenBalanceQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { poolAddress } = useLiquidStakingPool();
  const { name, balance, isBalanceLoading, usdBalance, mainBalance } =
    useStakeToken(poolAddress);

  const { apy, isLoading: isAPYLoading } = useGetLiquidStakingAPY(poolAddress);

  const usdMainBalance = useGetUSDAmount(mainTokenBalance);

  const annualEarning = useMemo(
    () => mainBalance.multipliedBy(apy.dividedBy(HUNDRED)),
    [mainBalance, apy],
  );

  const annualEarningUSD = useGetUSDAmount(annualEarning);

  return (
    <div className={classes.root}>
      <BorderedPaper className={cx(classes.card, classes.myStakedCard)}>
        <Typography alignSelf="center" fontWeight={600} variant="body1">
          {t(keys.myStaked, { token: name })}
        </Typography>

        <div className={classes.amount}>
          <Typography component="div" fontSize={36} fontWeight={600}>
            {isBalanceLoading ? (
              <Skeleton />
            ) : (
              balance.decimalPlaces(DEFAULT_DECIMAL_PLACES).toFormat()
            )}
          </Typography>

          <div className={classes.amounts}>
            {!mainBalance.isZero() && (
              <>
                <MainTokenIcon />

                <Typography ml={1} variant="body1">
                  {t(keys.unit.tokenValue, {
                    value: mainBalance
                      .decimalPlaces(DEFAULT_DECIMAL_PLACES)
                      .toFormat(),
                    token: t(keys.tokens.main),
                  })}
                </Typography>
              </>
            )}

            {!usdBalance.isZero() && (
              <>
                <div className={classes.divider} />

                <Typography variant="body1">
                  {t(keys.unit.$, {
                    value: usdBalance
                      .decimalPlaces(DECIMAL_PLACES_SHORT)
                      .toFormat(),
                  })}
                </Typography>
              </>
            )}
          </div>
        </div>
      </BorderedPaper>

      <BaseStat
        label={t(keys.annualEarning, { token: t(keys.tokens.main) })}
        topRightContent={
          isAPYLoading ? undefined : (
            <Chip
              color="secondary"
              label={t(keys.apy, {
                apy: apy.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
              })}
            />
          )
        }
        usdValue={annualEarningUSD}
        value={isBalanceLoading || isAPYLoading ? undefined : annualEarning}
      />

      <BaseStat
        label={t(keys.availableToStake, { token: t(keys.tokens.main) })}
        topRightContent={
          BUY_MAIN_MORE_LINK ? (
            <a
              className={classes.link}
              href={BUY_MAIN_MORE_LINK}
              rel="noreferrer"
              target="_blank"
            >
              <img alt={t(keys.buyMore)} src={buyMoreImage} />
            </a>
          ) : undefined
        }
        usdValue={usdMainBalance}
        value={
          isMainTokenBalanceLoading
            ? undefined
            : mainTokenBalance.decimalPlaces(DEFAULT_DECIMAL_PLACES)
        }
      />
    </div>
  );
}
